import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ArrowRight, Calendar, MapPin, Moon } from "lucide-react";
import { api } from "@/lib/api";
import SEO from "@/components/SEO";
import Mandala from "@/components/Mandala";
import { usePageImages } from "@/lib/pageImages";
import RegisterRetreat from "@/pages/RegisterRetreat";
import { SLUG, DEFAULT_IMAGES } from "@/components/winter/winterContent";
import { Opening, FireBand, WhoFor, Experiences, Belonging, Treasures, Itinerary } from "@/components/winter/WinterSections";
import { YourStay, MeetHost, Closing } from "@/components/winter/WinterStay";

function Hero({ img }) {
  return (
    <section className="relative min-h-[92vh] flex items-center overflow-hidden bg-aviana-ink" data-testid="ws-hero">
      <img src={img("hero")} alt="Women in cowgirl hats holding hands around the fire at dusk" className="absolute inset-0 w-full h-full object-cover object-center opacity-75" />
      <div className="absolute inset-0 bg-gradient-to-r from-aviana-ink/85 via-aviana-plumDeep/45 to-transparent" />
      <Mandala size={520} variant="rose" className="absolute -top-28 -right-32 text-aviana-goldSoft/15 hidden md:block" />
      <div className="container-aviana relative z-10 pt-32 pb-24 text-aviana-cream">
        <p className="overline text-aviana-goldSoft mb-6 animate-in fade-in duration-700" data-testid="ws-hero-subtitle">A Sacred Sisterhood Gathering at the Ranch</p>
        <h1 className="font-display text-5xl md:text-7xl lg:text-[92px] leading-[0.98] max-w-4xl">The Winter <span className="italic text-aviana-goldSoft">Solstice</span> Retreat</h1>
        <div className="mt-9 flex flex-wrap gap-x-8 gap-y-3 text-aviana-cream/85 text-[14px]" data-testid="ws-hero-dates">
          <span className="flex items-center gap-2"><Calendar size={16} className="text-aviana-goldSoft" /> December 18–20, 2026</span>
          <span className="flex items-center gap-2"><MapPin size={16} className="text-aviana-goldSoft" /> Galena, Illinois</span>
          <span className="flex items-center gap-2"><Moon size={16} className="text-aviana-goldSoft" /> Optional extended stay through Monday, December 21 — the Winter Solstice itself</span>
        </div>
        <p className="mt-9 font-serif italic text-2xl md:text-3xl text-aviana-cream/90 tracking-wide" data-testid="ws-hero-tagline">Rest. Reconnect. Create. Remember.</p>
        <a href="#register" className="btn-gold mt-11 inline-flex" data-testid="ws-hero-register">Join us by the fire <ArrowRight size={14} /></a>
      </div>
    </section>
  );
}

export default function WinterSolsticeRetreat() {
  const img = usePageImages("winter-solstice", DEFAULT_IMAGES);
  const [sp] = useSearchParams();
  const joinId = sp.get("join") || "";
  const [offering, setOffering] = useState(null);
  const [availability, setAvailability] = useState(null);
  const [room, setRoom] = useState("");

  useEffect(() => {
    api.get(`/offerings/${SLUG}`).then(({ data }) => {
      setOffering(data);
      return api.get(`/retreats/${data.id}/room-availability`);
    }).then(({ data }) => setAvailability(data.available)).catch(() => {});
  }, []);

  useEffect(() => {
    if (joinId) setTimeout(() => document.getElementById("register")?.scrollIntoView({ behavior: "smooth" }), 600);
  }, [joinId]);

  const choose = (key) => {
    setRoom(key);
    document.getElementById("register")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="bg-aviana-paper" data-testid="winter-solstice-page">
      <SEO
        title="The Winter Solstice Retreat · A Sacred Sisterhood Gathering in Galena, IL"
        description="An intimate Winter Solstice women's retreat at a log cabin ranch in Galena, Illinois — December 18–20, 2026. Sister circles, sound bath, horses, woodworking, hot tub, candlelight and nourishing meals by Holly."
        path="/retreats/winter-solstice-retreat"
        image={DEFAULT_IMAGES.opening}
        keywords="winter solstice retreat, women's retreat Illinois, Galena retreat, sisterhood retreat, mother daughter retreat, horse retreat, December retreat 2026"
        type="event"
      />
      <Hero img={img} />
      <Opening img={img} />
      <FireBand src={img("fire-band")} caption="The longest night, and the return of the light." />
      <WhoFor img={img} />
      <Experiences img={img} />
      <Belonging img={img} />
      <Treasures img={img} />
      <Itinerary img={img} />
      <YourStay img={img} offering={offering} availability={availability} onChoose={choose} />
      <MeetHost img={img} />
      <Closing img={img} />
      <section id="register" className="py-24 md:py-28 bg-aviana-paper scroll-mt-20" data-testid="ws-register">
        <RegisterRetreat slug={SLUG} embedded presetRoom={room} joinId={joinId} />
      </section>
    </div>
  );
}
